import { FunctionItem2D } from '../types/math';

export const functions2D: FunctionItem2D[] = [
  {
    id: 'quadratic',
    dimension: '2d',
    title: 'Standard Parabola',
    category: 'polynomial',
    formulaLatex: 'f(x) = x^2',
    dynamicFormulaLatex: (p) => {
      const a = p.a?.toFixed(1) ?? '1.0';
      const c = p.c?.toFixed(1) ?? '0.0';
      return `f(x) = ${a}x^2 ${parseFloat(c) >= 0 ? '+' : ''} ${c}`;
    },
    evaluate: (x, p) => {
      const a = p?.a ?? 1;
      const c = p?.c ?? 0;
      return a * x * x + c;
    },
    xDomain: [-5, 5],
    yDomain: [-2, 10],
    zeros: [0],
    intuition: {
      summary: 'U-shaped parabola with vertex at the origin and symmetric even growth.',
      keySignatures: [
        'Symmetric across the y-axis: f(-x) = f(x) (even function)',
        'Vertex at (0, 0) with a horizontal tangent',
        'Quadratic growth: rate of increase grows linearly with x'
      ],
      dominantBehavior: 'Grows quadratically as x → ±∞'
    },
    distractors: [
      { formulaLatex: 'f(x) = x^3', explanation: 'A cubic passes through quadrant III with rotational odd symmetry, not a U-shape.' },
      { formulaLatex: 'f(x) = |x|', explanation: 'Absolute value forms a sharp V-wedge at the origin, whereas x² is curved and smooth.' },
      { formulaLatex: 'f(x) = -x^2', explanation: 'A negative coefficient reflects the parabola downward into quadrants III and IV.' }
    ],
    parameters: {
      a: { min: -2, max: 3, default: 1, step: 0.1, label: 'Vertical Stretch (a)' },
      c: { min: -4, max: 4, default: 0, step: 0.2, label: 'Vertical Shift (c)' }
    }
  },
  {
    id: 'cubic_depressed',
    dimension: '2d',
    title: 'Cubic with Local Extrema',
    category: 'polynomial',
    formulaLatex: 'f(x) = x^3 - 3x',
    dynamicFormulaLatex: (p) => {
      const b = p.b?.toFixed(1) ?? '3.0';
      return `f(x) = x^3 - ${b}x`;
    },
    evaluate: (x, p) => {
      const b = p?.b ?? 3;
      return x * x * x - b * x;
    },
    xDomain: [-3.5, 3.5],
    yDomain: [-6, 6],
    zeros: [-1.73, 0, 1.73],
    intuition: {
      summary: 'S-shaped cubic with rotational symmetry, one local maximum, and one local minimum.',
      keySignatures: [
        'Rotational odd symmetry: f(-x) = -f(x) (180° rotation around origin)',
        'Local maximum at (-1, 2) and local minimum at (1, -2)',
        'Three distinct real roots at x = -√3, 0, +√3'
      ],
      dominantBehavior: 'Falls to -∞ as x → -∞, rises to +∞ as x → +∞'
    },
    distractors: [
      { formulaLatex: 'f(x) = x^3', explanation: 'Pure x³ is monotonic increasing with an inflection point at (0,0), having no local hill or valley.' },
      { formulaLatex: 'f(x) = -x^3 + 3x', explanation: 'Flipping the sign mirrors the curve, so it would fall from top-left to bottom-right.' },
      { formulaLatex: 'f(x) = x^4 - 2x^2', explanation: 'Degree 4 is W-shaped and even (symmetric across y-axis), with both ends pointing up.' }
    ],
    parameters: {
      b: { min: 0, max: 5, default: 3, step: 0.2, label: 'Linear Term (b)' }
    }
  },
  {
    id: 'reciprocal',
    dimension: '2d',
    title: 'Standard Hyperbola',
    category: 'polynomial',
    formulaLatex: 'f(x) = \\frac{1}{x}',
    evaluate: (x) => {
      if (Math.abs(x) < 0.001) return NaN;
      return 1 / x;
    },
    xDomain: [-6, 6],
    yDomain: [-6, 6],
    asymptotes: {
      vertical: [0],
      horizontal: [0]
    },
    intuition: {
      summary: 'Classic hyperbola in quadrants I and III with dual perpendicular asymptotes.',
      keySignatures: [
        'Vertical asymptote at x = 0: blows up to +∞ from the right, dives to -∞ from the left',
        'Horizontal asymptote at y = 0: flattens along the x-axis for large |x|',
        'Odd symmetry: rotating 180° around the origin leaves the curve unchanged'
      ],
      dominantBehavior: 'Singularity at x = 0; approaches 0 as |x| → ∞'
    },
    distractors: [
      { formulaLatex: 'f(x) = \\frac{1}{x^2}', explanation: '1/x² is always positive and stays entirely in quadrants I and II.' },
      { formulaLatex: 'f(x) = -\\frac{1}{x}', explanation: 'Negative 1/x lives in quadrants II and IV instead of I and III.' },
      { formulaLatex: 'f(x) = \\frac{1}{x - 2}', explanation: 'This shifts the vertical asymptote 2 units to the right at x = 2.' }
    ]
  },
  {
    id: 'reciprocal_squared',
    dimension: '2d',
    title: 'Inverse Square (Even Pole)',
    category: 'polynomial',
    formulaLatex: 'f(x) = \\frac{1}{x^2}',
    evaluate: (x) => {
      if (Math.abs(x) < 0.001) return NaN;
      return 1 / (x * x);
    },
    xDomain: [-5, 5],
    yDomain: [-1, 8],
    asymptotes: {
      vertical: [0],
      horizontal: [0]
    },
    intuition: {
      summary: 'Volcano-like peak blowing up to +∞ from both left and right due to even square.',
      keySignatures: [
        'Strictly positive: f(x) > 0 everywhere in its domain',
        'Vertical asymptote at x = 0 blowing to +∞ on BOTH sides',
        'Even symmetry: f(-x) = f(x)'
      ],
      dominantBehavior: 'Approaches 0 as |x| → ∞; blows up to +∞ at x = 0'
    },
    distractors: [
      { formulaLatex: 'f(x) = \\frac{1}{x}', explanation: '1/x drops into negative numbers in quadrant III.' },
      { formulaLatex: 'f(x) = -\\frac{1}{x^2}', explanation: 'Negative 1/x² would point downward into quadrant III and IV.' },
      { formulaLatex: 'f(x) = \\frac{1}{1 + x^2}', explanation: '1/(1+x²) has no vertical asymptote; it has a smooth finite peak at (0, 1).' }
    ]
  },
  {
    id: 'natural_exp',
    dimension: '2d',
    title: 'Natural Exponential Growth',
    category: 'transcendental',
    formulaLatex: 'f(x) = e^x',
    dynamicFormulaLatex: (p) => {
      const a = p.a?.toFixed(1) ?? '1.0';
      return `f(x) = e^{${a}x}`;
    },
    evaluate: (x, p) => {
      const a = p?.a ?? 1;
      return Math.exp(a * x);
    },
    xDomain: [-4, 4],
    yDomain: [-1, 8],
    asymptotes: {
      horizontal: [0]
    },
    intuition: {
      summary: 'Explosive acceleration upward for positive x, flattening to zero for negative x.',
      keySignatures: [
        'Horizontal asymptote at y = 0 as x → -∞',
        'Passes through y-intercept at (0, 1)',
        'Strictly positive: e^x > 0 everywhere'
      ],
      dominantBehavior: 'Grows exponentially as x → +∞, vanishes as x → -∞'
    },
    distractors: [
      { formulaLatex: 'f(x) = e^{-x}', explanation: 'e^(-x) is exponential decay: high on the left and flattening to 0 on the right.' },
      { formulaLatex: 'f(x) = -e^x', explanation: '-e^x is reflected across the x-axis, plunging downwards into negative numbers.' },
      { formulaLatex: 'f(x) = \\ln(x)', explanation: 'ln(x) is only defined for positive x, has a vertical asymptote at x = 0, and grows slowly.' }
    ],
    parameters: {
      a: { min: -2, max: 2, default: 1, step: 0.1, label: 'Growth Rate (a)' }
    }
  },
  {
    id: 'exp_decay',
    dimension: '2d',
    title: 'Exponential Decay',
    category: 'transcendental',
    formulaLatex: 'f(x) = e^{-x}',
    evaluate: (x) => Math.exp(-x),
    xDomain: [-3, 5],
    yDomain: [-1, 8],
    asymptotes: {
      horizontal: [0]
    },
    intuition: {
      summary: 'High values for negative x that asymptotically decay towards zero on the right.',
      keySignatures: [
        'Horizontal asymptote at y = 0 as x → +∞',
        'Passes through (0, 1)',
        'Monotonically decreasing everywhere'
      ],
      dominantBehavior: 'Explodes as x → -∞; flattens to 0 as x → +∞'
    },
    distractors: [
      { formulaLatex: 'f(x) = e^x', explanation: 'e^x grows towards the right and flattens to 0 towards the left.' },
      { formulaLatex: 'f(x) = -e^{-x}', explanation: '-e^(-x) is inverted below the x-axis.' },
      { formulaLatex: 'f(x) = \\frac{1}{x}', explanation: '1/x has a vertical singularity at x = 0, while e^(-x) is perfectly smooth.' }
    ]
  },
  {
    id: 'natural_log',
    dimension: '2d',
    title: 'Natural Logarithm',
    category: 'transcendental',
    formulaLatex: 'f(x) = \\ln(x)',
    evaluate: (x) => {
      if (x <= 0.001) return NaN;
      return Math.log(x);
    },
    xDomain: [-1, 8],
    yDomain: [-4, 3],
    zeros: [1],
    asymptotes: {
      vertical: [0]
    },
    intuition: {
      summary: 'Only exists on the right half-plane, diving to -∞ at x = 0 and creeping up slowly.',
      keySignatures: [
        'Domain is strictly positive: x > 0',
        'Vertical asymptote at x = 0 plunging to -∞',
        'x-intercept exactly at (1, 0)'
      ],
      dominantBehavior: 'Sub-linear slow creep to +∞ as x → ∞'
    },
    distractors: [
      { formulaLatex: 'f(x) = \\sqrt{x}', explanation: '√x starts at (0, 0) with a finite value, rather than diving to -∞ at x = 0.' },
      { formulaLatex: 'f(x) = e^x', explanation: 'e^x is defined for all x and grows explosively, the exact inverse of ln(x).' },
      { formulaLatex: 'f(x) = -\\ln(x)', explanation: '-ln(x) shoots up to +∞ at x = 0 and decays into negative values for x > 1.' }
    ]
  },
  {
    id: 'sigmoid',
    dimension: '2d',
    title: 'Logistic Sigmoid',
    category: 'transcendental',
    formulaLatex: 'f(x) = \\frac{1}{1 + e^{-x}}',
    evaluate: (x) => 1 / (1 + Math.exp(-x)),
    xDomain: [-6, 6],
    yDomain: [-0.5, 1.5],
    asymptotes: {
      horizontal: [0, 1]
    },
    intuition: {
      summary: 'Smooth S-shaped activation function bounded strictly between 0 and 1.',
      keySignatures: [
        'Two horizontal asymptotes: y = 0 (as x → -∞) and y = 1 (as x → +∞)',
        'Symmetric inflection point at (0, 0.5)',
        'Crucial model for probability, neural network activations, and population saturation'
      ],
      dominantBehavior: 'Saturates at 0 and 1'
    },
    distractors: [
      { formulaLatex: 'f(x) = \\tanh(x)', explanation: 'tanh(x) is bounded between -1 and +1, passing through (0, 0).' },
      { formulaLatex: 'f(x) = \\arctan(x)', explanation: 'arctan(x) is bounded between -π/2 and +π/2 and passes through (0, 0).' },
      { formulaLatex: 'f(x) = \\frac{1}{1 + x^2}', explanation: '1/(1+x²) is a bell-shaped curve with peak at (0, 1), not an S-curve.' }
    ]
  },
  {
    id: 'gaussian',
    dimension: '2d',
    title: 'Gaussian Bell Curve',
    category: 'transcendental',
    formulaLatex: 'f(x) = e^{-x^2}',
    dynamicFormulaLatex: (p) => {
      const s = p.s?.toFixed(1) ?? '1.0';
      return `f(x) = e^{-\\frac{x^2}{${s}}}`;
    },
    evaluate: (x, p) => {
      const s = p?.s ?? 1;
      return Math.exp(-(x * x) / s);
    },
    xDomain: [-4, 4],
    yDomain: [-0.2, 1.3],
    asymptotes: {
      horizontal: [0]
    },
    intuition: {
      summary: 'Famous normal distribution bell curve with smooth peak and exponential tail decay.',
      keySignatures: [
        'Global maximum peak at (0, 1)',
        'Even symmetry: f(-x) = f(x)',
        'Inflection points at x = ±1/√2 where curvature changes from convex to concave'
      ],
      dominantBehavior: 'Decays super-exponentially to 0 as |x| → ∞'
    },
    distractors: [
      { formulaLatex: 'f(x) = \\frac{1}{1 + x^2}', explanation: 'The Cauchy distribution 1/(1+x²) has much fatter polynomial tails than the steep Gaussian.' },
      { formulaLatex: 'f(x) = 1 - x^2', explanation: '1 - x² is an inverted parabola that plunges through the x-axis to -∞.' },
      { formulaLatex: 'f(x) = e^{-|x|}', explanation: 'e^(-|x|) has a sharp pointed cusp at x = 0, whereas Gaussian is rounded and smooth.' }
    ],
    parameters: {
      s: { min: 0.2, max: 3, default: 1, step: 0.2, label: 'Variance Scale (s)' }
    }
  },
  {
    id: 'sine_wave',
    dimension: '2d',
    title: 'Standard Sine Wave',
    category: 'trigonometric',
    formulaLatex: 'f(x) = \\sin(x)',
    dynamicFormulaLatex: (p) => {
      const a = p.a?.toFixed(1) ?? '1.0';
      const b = p.b?.toFixed(1) ?? '1.0';
      return `f(x) = ${a}\\sin(${b}x)`;
    },
    evaluate: (x, p) => {
      const a = p?.a ?? 1;
      const b = p?.b ?? 1;
      return a * Math.sin(b * x);
    },
    xDomain: [-7, 7],
    yDomain: [-2, 2],
    zeros: [0, 3.14, -3.14, 6.28, -6.28],
    intuition: {
      summary: 'Smooth periodic wave passing directly through the origin with slope 1.',
      keySignatures: [
        'Passes through (0, 0) with odd symmetry: f(-x) = -f(x)',
        'Period is 2π ≈ 6.28',
        'Oscillates between amplitude limits -1 and +1'
      ],
      dominantBehavior: 'Endless periodic oscillation'
    },
    distractors: [
      { formulaLatex: 'f(x) = \\cos(x)', explanation: 'Cosine starts at a peak (0, 1) at the origin rather than crossing (0, 0).' },
      { formulaLatex: 'f(x) = -\\sin(x)', explanation: '-sin(x) goes downward first into quadrant IV instead of climbing into quadrant I.' },
      { formulaLatex: 'f(x) = \\sin(2x)', explanation: 'sin(2x) has twice the frequency, completing a full cycle in π ≈ 3.14.' }
    ],
    parameters: {
      a: { min: 0.5, max: 2, default: 1, step: 0.25, label: 'Amplitude (a)' },
      b: { min: 0.5, max: 3, default: 1, step: 0.5, label: 'Frequency (b)' }
    }
  },
  {
    id: 'cosine_wave',
    dimension: '2d',
    title: 'Standard Cosine Wave',
    category: 'trigonometric',
    formulaLatex: 'f(x) = \\cos(x)',
    evaluate: (x) => Math.cos(x),
    xDomain: [-7, 7],
    yDomain: [-2, 2],
    zeros: [1.57, -1.57, 4.71, -4.71],
    intuition: {
      summary: 'Periodic wave starting at a crest at x = 0 with even symmetry.',
      keySignatures: [
        'Passes through peak at (0, 1)',
        'Even symmetry: f(-x) = f(x)',
        'Period 2π with roots at odd multiples of π/2'
      ],
      dominantBehavior: 'Endless periodic oscillation starting at crest'
    },
    distractors: [
      { formulaLatex: 'f(x) = \\sin(x)', explanation: 'Sine starts at (0, 0), while cosine starts at its crest (0, 1).' },
      { formulaLatex: 'f(x) = -\\cos(x)', explanation: '-cos(x) starts at a trough (0, -1) at the origin.' },
      { formulaLatex: 'f(x) = \\cos(x - \\pi)', explanation: 'A phase shift of π inverts the wave, placing a trough at x = 0.' }
    ]
  },
  {
    id: 'tangent',
    dimension: '2d',
    title: 'Tangent (Periodic Poles)',
    category: 'trigonometric',
    formulaLatex: 'f(x) = \\tan(x)',
    evaluate: (x) => {
      // Check near asymptotes at pi/2 + k*pi
      const mod = ((x % Math.PI) + Math.PI) % Math.PI;
      if (Math.abs(mod - Math.PI / 2) < 0.05) return NaN;
      const val = Math.tan(x);
      return Math.abs(val) > 15 ? NaN : val;
    },
    xDomain: [-5, 5],
    yDomain: [-6, 6],
    asymptotes: {
      vertical: [-4.71, -1.57, 1.57, 4.71]
    },
    intuition: {
      summary: 'Periodic upward branches repeating every π, separated by vertical asymptotes.',
      keySignatures: [
        'Vertical asymptotes at x = ±π/2, ±3π/2, ... where cos(x) = 0',
        'Always strictly increasing within each continuous branch',
        'Period is π (half the period of sine and cosine)'
      ],
      dominantBehavior: 'Repeats branches with infinite vertical leaps every π'
    },
    distractors: [
      { formulaLatex: 'f(x) = \\cot(x)', explanation: 'cot(x) is strictly decreasing on its branches and has asymptotes at x = 0, ±π.' },
      { formulaLatex: 'f(x) = x^3', explanation: 'x³ is a single smooth cubic curve without periodic repeating branches or asymptotes.' },
      { formulaLatex: 'f(x) = \\arctan(x)', explanation: 'arctan(x) is a single bounded curve with horizontal asymptotes at ±π/2.' }
    ]
  },
  {
    id: 'damped_oscillation',
    dimension: '2d',
    title: 'Damped Sine Oscillation',
    category: 'trigonometric',
    formulaLatex: 'f(x) = e^{-0.3x}\\sin(3x)',
    evaluate: (x) => {
      if (x < -1) return NaN;
      return Math.exp(-0.3 * x) * Math.sin(3 * x);
    },
    xDomain: [-0.5, 12],
    yDomain: [-1.5, 1.5],
    intuition: {
      summary: 'A vibrating oscillation trapped inside a shrinking exponential envelope.',
      keySignatures: [
        'Bounded between exponential envelopes +e^(-0.3x) and -e^(-0.3x)',
        'Amplitude decays exponentially towards zero over time (x)',
        'Fundamental model for shock absorbers, guitar strings, and RLC circuits'
      ],
      dominantBehavior: 'Oscillates with decaying amplitude toward 0'
    },
    distractors: [
      { formulaLatex: 'f(x) = e^{0.3x}\\sin(3x)', explanation: 'Positive exponent causes resonance explosion: oscillations blow up over time.' },
      { formulaLatex: 'f(x) = \\frac{\\sin(3x)}{x}', explanation: 'Sinc function decays as 1/x (polynomial) rather than fast exponential e^(-kx).' },
      { formulaLatex: 'f(x) = e^{-0.3x}', explanation: 'The pure exponential decay has no oscillations—it is monotonic.' }
    ]
  },
  {
    id: 'sinc_2d',
    dimension: '2d',
    title: 'Cardinal Sinc Function',
    category: 'famous',
    formulaLatex: 'f(x) = \\frac{\\sin(x)}{x}',
    evaluate: (x) => {
      if (Math.abs(x) < 0.0001) return 1;
      return Math.sin(x) / x;
    },
    xDomain: [-15, 15],
    yDomain: [-0.4, 1.2],
    zeros: [3.14, -3.14, 6.28, -6.28],
    intuition: {
      summary: 'A central ripple cresting smoothly at y = 1 and radiating ripples that fade as 1/x.',
      keySignatures: [
        'Removable singularity at origin: lim(x→0) sin(x)/x = 1',
        'Even symmetry: f(-x) = f(x)',
        'Zero-crossings at every non-zero integer multiple of π (x = ±π, ±2π, ...)'
      ],
      dominantBehavior: 'Peak of 1 at center, decaying sinusoidal ripples outward'
    },
    distractors: [
      { formulaLatex: 'f(x) = \\frac{\\cos(x)}{x}', explanation: 'cos(x)/x has a severe vertical pole at x = 0 blowing up to ±∞.' },
      { formulaLatex: 'f(x) = e^{-x^2}\\sin(x)', explanation: 'This is an odd function (anti-symmetric) that passes through (0, 0).' },
      { formulaLatex: 'f(x) = \\sin(x)', explanation: 'Pure sin(x) never decays; its amplitude stays constant at 1 forever.' }
    ]
  },
  {
    id: 'witch_of_agnesi',
    dimension: '2d',
    title: 'Witch of Agnesi',
    category: 'famous',
    formulaLatex: 'f(x) = \\frac{4}{x^2 + 1}',
    evaluate: (x) => 4 / (x * x + 1),
    xDomain: [-6, 6],
    yDomain: [-0.5, 5],
    asymptotes: {
      horizontal: [0]
    },
    intuition: {
      summary: 'Famous bell-shaped algebraic curve with peak at (0, 4) named by Maria Agnesi (1748).',
      keySignatures: [
        'Global maximum at (0, 4)',
        'Horizontal asymptote at y = 0 as |x| → ∞',
        'Algebraic rational function: slower tail decay (~1/x²) compared to exponential Gaussian'
      ],
      dominantBehavior: 'Smooth symmetrical algebraic peak with horizontal asymptote'
    },
    distractors: [
      { formulaLatex: 'f(x) = \\frac{4}{x^2 - 1}', explanation: 'Minus in the denominator creates vertical asymptotes at x = ±1!' },
      { formulaLatex: 'f(x) = 4e^{-x^2}', explanation: 'Exponential Gaussian decays much faster into the tails.' },
      { formulaLatex: 'f(x) = 4 - x^2', explanation: 'An inverted parabola plunges through the axis into negative numbers.' }
    ]
  },
  {
    id: 'abs_val',
    dimension: '2d',
    title: 'Absolute Value V-Wedge',
    category: 'polynomial',
    formulaLatex: 'f(x) = |x| - 2',
    evaluate: (x) => Math.abs(x) - 2,
    xDomain: [-6, 6],
    yDomain: [-3, 5],
    zeros: [-2, 2],
    intuition: {
      summary: 'Sharp piecewise linear corner forming a distinct V-shape.',
      keySignatures: [
        'Sharp, non-differentiable corner vertex at (0, -2)',
        'Constant slope +1 for x > 0, constant slope -1 for x < 0',
        'Even symmetry: f(-x) = f(x)'
      ],
      dominantBehavior: 'Straight diagonal arms extending upward'
    },
    distractors: [
      { formulaLatex: 'f(x) = x^2 - 2', explanation: 'x² - 2 has a smooth curved bottom with continuous derivative, not a sharp point.' },
      { formulaLatex: 'f(x) = |x - 2|', explanation: '|x - 2| shifts the vertex horizontally to the right at (2, 0).' },
      { formulaLatex: 'f(x) = -|x| + 2', explanation: 'Negative sign points the V downward like a roof.' }
    ]
  },
  {
    id: 'square_root',
    dimension: '2d',
    title: 'Square Root Curve',
    category: 'polynomial',
    formulaLatex: 'f(x) = \\sqrt{x}',
    evaluate: (x) => {
      if (x < 0) return NaN;
      return Math.sqrt(x);
    },
    xDomain: [-1, 9],
    yDomain: [-1, 4],
    zeros: [0],
    intuition: {
      summary: 'Half of a sideways parabola starting cleanly at the origin and bending gently.',
      keySignatures: [
        'Only defined for non-negative numbers: x ≥ 0',
        'Starts at endpoint (0, 0) with an infinite vertical tangent',
        'Concave down everywhere: rate of growth steadily slows'
      ],
      dominantBehavior: 'Slow sublinear growth for positive x'
    },
    distractors: [
      { formulaLatex: 'f(x) = x^2', explanation: 'x² grows quadratically and is defined for negative numbers as well.' },
      { formulaLatex: 'f(x) = \\sqrt[3]{x}', explanation: 'Cube root is defined for negative numbers and has an S-shape through the origin.' },
      { formulaLatex: 'f(x) = \\ln(x)', explanation: 'ln(x) has a vertical asymptote plunging to -∞ at x = 0, whereas √0 = 0.' }
    ]
  }
];
